import { Component, type ErrorInfo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isApiError } from "@/api/client";
import { t } from "@/i18n/ko";

const STALE_MS = 10 * 60 * 1000;
const QUERY_RETRY = 3;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_MS,
        // 0.5절: 쿼리 3회, 뮤테이션 자동 재시도 없음. 4xx는 재시도해도 같다.
        retry: (count, err) => count < QUERY_RETRY && !(isApiError(err) && err.status >= 400 && err.status < 500),
        refetchOnWindowFocus: false,
      },
      mutations: { retry: 0 },
    },
  });
}

export const queryClient = createQueryClient();

type BoundaryState = { error: Error | null };

class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[app]", error, info.componentStack);
  }
  render() {
    if (this.state.error) return <div className="page-placeholder">{t("error.generic")}</div>;
    return this.props.children;
  }
}

export function Providers({ children, client = queryClient }: { children: ReactNode; client?: QueryClient }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>{children}</QueryClientProvider>
    </ErrorBoundary>
  );
}
